import React, { useRef, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { FormControl, Typography } from '@mui/material';
import PrimaryBtn from '../shared/Buttons/PrimaryButton';
import TextFieldStyled from '../shared/TextFieldStyled';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { SubmitButtonWrapper } from '../styled/Form/SubmitButtonWrapper';
import { AuthPageTitle } from '../shared/Typography/AuthenticationsPageTitle';
import useForgotPassword from '../../hooks/authentication/useForgotPassword';
import { ResetPasswordParams } from '../../models/Authentication/ForgotPassword';
import SnackbarUtils from '../../utils/SnackbarUtils';

export const ForgotPasswordLandingForm: React.FC = () => {
    const {
        register,
        handleSubmit,
        watch,
        formState: { errors },
    } = useForm<FormValues>();

    let [searchParams, setSearchParams] = useSearchParams();
    const email = searchParams.get('email');
    const { token } = useParams();
    const forgotPassword = useForgotPassword();
    const navigate = useNavigate();

    const { t } = useTranslation();
    const [isLoading, setIsLoading] = useState(false);

    const getTranslate = (key) => {
        return t(key).toString();
    };

    const password = useRef({});
    password.current = watch('password', '');

    const onSubmit: SubmitHandler<FormValues> = (data) => {
        setIsLoading(true);
        const params: ResetPasswordParams = {
            password: data.password,
            password_confirmation: data.password_confirmation,
            email: email,
            token: token,
        };
        forgotPassword
            .sendResetPassword(params)
            .then((resp) => {
                SnackbarUtils.success(
                    t('NOTIFICATION.PASSWORD_CHANGED').toString()
                );
                navigate('/login', { replace: true });
            })
            .catch((e) => {
                setIsLoading(false);
            });
    };

    return (
        <>
            <AuthPageTitle>
                {t('FORGOT_PASSWORD_PAGE.TITLE').toString()}
            </AuthPageTitle>

            <>
                <Typography paragraph>{t('FORGOT_PASSWORD_PAGE.NEW_PASSWORD').toString()}</Typography>
                <form onSubmit={handleSubmit(onSubmit)}>
                    {/* Password */}
                    <Typography paragraph>
                        {t(
                            'REGISTRATION_FORM_FIELDS.PASSWORD_FORMS'
                        ).toString()}
                    </Typography>
                    <FormControl fullWidth>
                        <TextFieldStyled
                            label={t(
                                'REGISTRATION_FORM_FIELDS.PASSWORD'
                            ).toString()}
                            error={!!errors.password}
                            type="password"
                            {...register('password', {
                                required: t(
                                    'FORM_VALIDATION_ERROR.REQUIRED'
                                ).toString(),
                                minLength: {
                                    value: 5,
                                    message: t(
                                        'FORM_VALIDATION_ERROR.PASSWORD_MIN_LENGTH'
                                    ).toString(),
                                },
                            })}
                            helperText={
                                errors.password ? errors.password.message : ' '
                            }
                        />
                    </FormControl>
                    {/* Retry Password */}
                    <FormControl fullWidth>
                        <TextFieldStyled
                            label={t(
                                'REGISTRATION_FORM_FIELDS.RETRY_PASSWORD'
                            ).toString()}
                            error={!!errors.password_confirmation}
                            type="password"
                            {...register('password_confirmation', {
                                required: t(
                                    'FORM_VALIDATION_ERROR.REQUIRED'
                                ).toString(),
                                validate: (value) =>
                                    value === password.current ||
                                    t(
                                        'FORM_VALIDATION_ERROR.PASSWORD_MISMATCH'
                                    ).toString(),
                            })}
                            helperText={
                                errors.password_confirmation
                                    ? errors.password_confirmation.message
                                    : ' '
                            }
                        />
                    </FormControl>
                    <SubmitButtonWrapper>
                        <PrimaryBtn type="submit" disabled={isLoading}>
                            {t('FORGOT_PASSWORD_PAGE.NEW_PASSWORD_SEND').toString()}
                        </PrimaryBtn>
                    </SubmitButtonWrapper>
                </form>
                <Typography paragraph>
                    <Link to="/login">
                        {t('FORGOT_PASSWORD_PAGE.BACK_TO_LOGIN').toString()}
                    </Link>
                </Typography>
            </>
        </>
    );
};

type FormValues = {
    password: string;
    password_confirmation: string;
};
